import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { fetchShoppingItems, addShoppingItem, purchaseShoppingItem, deleteShoppingItem } from '../lib/shopping.js';
import { subscribeToShopping } from '../lib/realtime.js';

export function ShoppingPage({ theme, profile, partner, pair }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchShoppingItems().then(items => {
      setItems(items);
      setLoading(false);
    });
  }, []);

  // Real-time subscription
  useEffect(() => {
    const unsubscribe = subscribeToShopping(({ event, row }) => {
      if (event === 'INSERT') {
        setItems(prev => prev.find(i => i.id === row.id) ? prev : [row, ...prev]);
      } else if (event === 'UPDATE') {
        if (row.purchased) {
          setItems(prev => prev.filter(i => i.id !== row.id));
        } else {
          setItems(prev => prev.map(i => i.id === row.id ? row : i));
        }
      } else if (event === 'DELETE') {
        setItems(prev => prev.filter(i => i.id !== row.id));
      }
    });
    return unsubscribe;
  }, []);

  async function handleAdd() {
    if (!newItem.trim() || submitting) return;
    setSubmitting(true);
    try {
      const added = await addShoppingItem(newItem.trim(), pair?.id || null);
      setItems(prev => prev.find(i => i.id === added.id) ? prev : [added, ...prev]);
      setNewItem('');
    } catch (e) {
      alert('Fejl: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePurchase(itemId) {
    try {
      await purchaseShoppingItem(itemId);
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (e) {
      alert('Fejl: ' + e.message);
    }
  }

  async function handleDelete(itemId) {
    try {
      await deleteShoppingItem(itemId);
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (e) {
      alert('Fejl: ' + e.message);
    }
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700 }}>Indkøb</h1>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: theme.textMuted }}>
        {pair ? `Delt med ${partner?.name}` : 'Din personlige liste — bliver delt når du parrer dig op'}
      </p>

      {/* Add input */}
      <div style={{
        display: 'flex',
        gap: 6,
        marginBottom: 16,
      }}>
        <input
          type="text"
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          placeholder="Tilføj vare..."
          style={{
            flex: 1,
            padding: '12px 14px',
            background: theme.bgCard,
            border: `1px solid ${theme.borderSubtle}`,
            borderRadius: 10,
            color: theme.text,
            fontSize: 14,
            outline: 'none',
          }}
        />
        <button
          onClick={handleAdd}
          disabled={!newItem.trim() || submitting}
          style={{
            background: theme.coral,
            color: 'white',
            border: 'none',
            padding: '0 14px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            cursor: newItem.trim() ? 'pointer' : 'not-allowed',
            opacity: newItem.trim() ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Plus size={16} />
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ color: theme.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>
          Henter listen...
        </div>
      ) : items.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '32px 20px',
          background: theme.bgCard,
          border: `1px solid ${theme.borderSubtle}`,
          borderRadius: 12,
          color: theme.textMuted,
          fontSize: 13,
        }}>
          {pair
            ? 'Listen er tom. Tilføj jeres første vare ovenfor.'
            : 'Listen er tom. Tilføj din første vare ovenfor.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(item => {
            const addedByMe = item.addedBy === profile?.id;
            return (
              <div key={item.id} style={{
                background: theme.bgCard,
                border: `1px solid ${theme.borderSubtle}`,
                borderRadius: 10,
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <button
                  onClick={() => handlePurchase(item.id)}
                  style={{
                    width: 22, height: 22,
                    borderRadius: '50%',
                    border: `2px solid ${theme.textDim}`,
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: 0,
                    flexShrink: 0,
                  }}
                  title="Markér som købt"
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: theme.text }}>{item.name}</div>
                  {pair && (
                    <div style={{ fontSize: 10, color: theme.textDim, marginTop: 2 }}>
                      {addedByMe ? 'Du tilføjede' : `${partner?.name} tilføjede`}
                    </div>
                  )}
                </div>
                {addedByMe && (
                  <button
                    onClick={() => handleDelete(item.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: theme.textDim,
                      cursor: 'pointer',
                      padding: 5,
                    }}
                    title="Slet"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {items.length > 0 && (
        <div style={{
          marginTop: 16,
          padding: '10px 14px',
          background: `${theme.coral}10`,
          border: `1px solid ${theme.coral}30`,
          borderRadius: 10,
          fontSize: 11,
          color: theme.coral,
          textAlign: 'center',
        }}>
          {items.length} {items.length === 1 ? 'vare' : 'varer'} på listen
        </div>
      )}

      {!pair && items.length > 0 && (
        <div style={{
          marginTop: 12,
          padding: '10px 14px',
          background: `${theme.gold}10`,
          border: `1px solid ${theme.gold}30`,
          borderRadius: 10,
          fontSize: 11,
          color: theme.gold,
          textAlign: 'center',
          lineHeight: 1.5,
        }}>
          Når du parrer dig med en partner, bliver din liste automatisk delt med dem.
        </div>
      )}
    </div>
  );
}
